/**
 * QuantiteInput — Champ de saisie de quantité intelligent
 *
 * Deux modes selon l'article :
 *
 * ┌─ Mode NORMAL (allow_pcs_cts = false) ──────────────────────────────────┐
 * │  TextField numérique classique. Valeur stockée = valeur saisie.        │
 * └────────────────────────────────────────────────────────────────────────┘
 *
 * ┌─ Mode CTS-PCS (allow_pcs_cts = true, pieces_par_carton > 0) ───────────┐
 * │  L'utilisateur tape au format  <cartons>-<pièces>  ex: "1-2"           │
 * │  La valeur stockée en DB est un décimal :                               │
 * │    valeur = cartons + pièces / pieces_par_carton                        │
 * │    ex: "1-2" avec ppc=12  →  1 + 2/12 = 1.1667                        │
 * │                                                                          │
 * │  Cas particuliers supportés :                                           │
 * │    "3"     →  3 cartons entiers (= 3.0 stocké)                         │
 * │    "1-2"   →  1 carton + 2 pièces (tiret non en position 0)            │
 * │    "-1-2"  →  -1 carton + 2 pièces (tiret en position 0 = signe négatif│
 * │               du carton, 2ème tiret = séparateur)                       │
 * │    "-5"    →  nombre négatif brut (un seul tiret en position 0)        │
 * └────────────────────────────────────────────────────────────────────────┘
 *
 * Comportement clé :
 *  - Pendant la frappe : parse en temps réel, erreur affichée si invalide,
 *    mais onChange n'est PAS appelé si la saisie est incomplète ("1-", "-").
 *    Cela évite que l'effet de sync externe ne vienne écraser la frappe en cours.
 *  - Au blur : normalise l'affichage (ex: "1-2" reste "1-2", décimal invalide
 *    revient à la dernière valeur valide).
 *  - Sélection depuis un tableau (MyTable) : la prop `key` dans le parent
 *    force le remount → useState lazy initializer → affichage immédiat correct.
 *  - Helper text : visible seulement en focus, positionné en absolu pour ne
 *    pas décaler la hauteur du formulaire.
 *
 * Dépendances :
 *  - formatQuantiteEditable() dans utils/quantiteUtils.ts  → décimal → "1-2" (éditable)
 *  - Article.allow_pcs_cts et Article.pieces_par_carton doivent être présents
 *    dans le JSON retourné par la SP (sp_select_ligne_document_vente inclut
 *    ces champs depuis la migration 2026-03-20_article_pcs_cts_dans_ligne_json)
 *
 * Usage :
 *  <QuantiteInput
 *    key={`qty-${selectedLigne?.id ?? resetKey}`}   // ← obligatoire dans les forms
 *    article={formData.article}
 *    value={formData.quantite}
 *    onChange={(v) => setFormData(prev => ({ ...prev, quantite: v }))}
 *    max={ligne.quantite_restante}   // optionnel : borne supérieure
 *  />
 */

import React, { useEffect, useRef, useState } from 'react';
import { Box, TextField, TextFieldProps } from '@mui/material';
import { Article } from '../models/models';
import { formatQuantiteEditable } from '../utils/quantiteUtils';

interface QuantiteInputProps
  extends Omit<TextFieldProps, 'value' | 'onChange' | 'type'> {
  article: Article | null | undefined;
  /** Valeur décimale stockée en DB (ex: 1.1667 pour "1-2" avec ppc=12) */
  value: number;
  /** Callback avec la valeur décimale calculée */
  onChange: (value: number) => void;
  /** Borne supérieure (ex: quantite_restante). Bloque la saisie au-delà. */
  max?: number;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Trouve l'index du tiret SÉPARATEUR cts-pcs dans la chaîne, ou -1 si absent.
 *
 * Logique :
 *   0 tiret          → -1  (nombre pur, ex: "5")
 *   1 tiret pos > 0  → cet index  (ex: "1-2" → 1)
 *   1 tiret pos = 0  → -1  (nombre négatif, ex: "-5")
 *   2 tirets, 1er en pos 0 → 2ème index  (ex: "-1-2" → 2, carton négatif)
 *   autres cas       → -1  (ambigu, traité comme nombre brut)
 */
function findSeparatorIndex(s: string): number {
  const dashes: number[] = [];
  for (let i = 0; i < s.length; i++) if (s[i] === '-') dashes.push(i);
  if (dashes.length === 0) return -1;
  if (dashes.length === 1) return dashes[0] === 0 ? -1 : dashes[0];
  if (dashes.length === 2 && dashes[0] === 0) return dashes[1];
  return -1;
}

/**
 * Parse une saisie brute en valeur décimale.
 *
 * Formats acceptés (mode cts-pcs) :
 *   "1-2"  → { value: 1 + 2/ppc, error: null }
 *   "-1-2" → { value: -1 + 2/ppc, error: null }
 *   "3"    → { value: 3, error: null }
 *   "1-15" avec ppc=12 → { value: 0, error: "Pièces : 0 à 11" }
 */
function parseInput(raw: string, ppc: number): { value: number; error: string | null } {
  const trimmed = raw.trim();
  const sepIdx = findSeparatorIndex(trimmed);

  if (sepIdx > 0) {
    // Format cts-pcs détecté
    const c = parseInt(trimmed.slice(0, sepIdx));
    const p = parseInt(trimmed.slice(sepIdx + 1));
    if (isNaN(c) || isNaN(p))
      return { value: 0, error: 'Format invalide' };
    if (p < 0 || p >= ppc)
      return { value: 0, error: `Pièces : 0 à ${ppc - 1}` };
    return { value: c + p / ppc, error: null };
  }

  // Nombre brut (entier ou décimal, positif ou négatif)
  const v = parseFloat(trimmed);
  if (isNaN(v)) return { value: 0, error: 'Valeur invalide' };
  return { value: v, error: null };
}

// ─── Component ───────────────────────────────────────────────────────────────

const QuantiteInput: React.FC<QuantiteInputProps> = ({
  article,
  value,
  onChange,
  max,
  label = 'Quantité',
  size = 'small',
  disabled = false,
  fullWidth = true,
  ...rest
}) => {
  // Dérivé de l'article — détermine quel mode d'affichage utiliser
  const ppc = article?.pieces_par_carton ?? null;
  const usePcsCts = !!(article?.allow_pcs_cts && ppc && ppc > 0);
  const uniteCarton = article?.unite || 'cts';
  const unitePiece = article?.unite_sec || 'pcs';

  // focused.current : vrai pendant que l'utilisateur tape.
  // Utilisé dans handleBlur pour savoir si on doit normaliser.
  // useRef (pas useState) pour ne pas déclencher de re-render.
  const focused = useRef(false);

  // isFocused : copie booléenne pour le rendu du helper text.
  // Ne contrôle PAS le sync externe (voir useEffect ci-dessous).
  const [isFocused, setIsFocused] = useState(false);

  // inputValue : ce qui s'affiche dans le TextField.
  // Initialisé via lazy initializer au moment du (re)mount.
  // Cela garantit un affichage immédiat correct lors d'une sélection
  // depuis MyTable (via la prop key qui force le remount dans le parent).
  const [inputValue, setInputValue] = useState<string>(() => {
    if (usePcsCts && ppc) return formatQuantiteEditable(value, article);
    return value === 0 ? '' : String(value);
  });

  const [error, setError] = useState<string | null>(null);

  // Sync depuis une valeur externe (Redux, sélection de ligne, etc.)
  // Déclenché quand value, usePcsCts, ppc ou article.id changent.
  //
  // POURQUOI pas de guard focused.current ici :
  //   onChange n'est PAS appelé lors d'une saisie incomplète ("1-", "-")
  //   donc value ne change pas pendant la frappe partielle → l'effet
  //   ne se déclenche pas et n'écrase pas ce que l'utilisateur tape.
  useEffect(() => {
    if (usePcsCts && ppc) {
      setInputValue(formatQuantiteEditable(value, article));
    } else {
      setInputValue(value === 0 ? '' : String(value));
    }
    setError(null);
  }, [value, usePcsCts, ppc, article?.id]);

  // ── Gestion de la frappe ─────────────────────────────────────────────────

  const handleChange = (raw: string) => {
    setInputValue(raw); // toujours afficher ce que l'utilisateur tape

    if (!usePcsCts || !ppc) {
      // Mode normal : parse direct et remonte la valeur
      const v = parseFloat(raw) || 0;
      onChange(max !== undefined ? Math.min(v, max) : v);
      return;
    }

    // Mode cts-pcs : on ne remonte rien pour les saisies incomplètes
    // afin d'éviter que l'effet de sync ne réécrive pendant la frappe.
    if (raw === '' || raw === '-') return;

    const parsed = parseInput(raw, ppc);
    if (parsed.error) {
      setError(parsed.error); // feedback visuel, non bloquant
    } else {
      const capped = max !== undefined ? Math.min(parsed.value, max) : parsed.value;
      onChange(capped);
    }
  };

  // ── Normalisation à la perte du focus ────────────────────────────────────

  const handleBlur = () => {
    focused.current = false;
    setIsFocused(false);

    if (!usePcsCts || !ppc) {
      // Mode normal : normalise (ex: "" → 0, supprime les zéros superflus)
      const v = parseFloat(inputValue) || 0;
      const capped = max !== undefined ? Math.min(v, max) : v;
      setInputValue(capped === 0 ? '' : String(capped));
      onChange(capped);
      setError(null);
      return;
    }

    if (inputValue === '' || inputValue === '-') {
      // Saisie vide ou signe seul → remise à zéro
      setInputValue('0');
      onChange(0);
      setError(null);
      return;
    }

    const parsed = parseInput(inputValue, ppc);
    if (parsed.error) {
      // Saisie invalide → on remet la dernière valeur connue (prop value)
      setInputValue(formatQuantiteEditable(value, article));
      setError(null);
    } else {
      // Saisie valide → normalise l'affichage (ex: "01-2" → "1-2")
      const capped = max !== undefined ? Math.min(parsed.value, max) : parsed.value;
      onChange(capped);
      setInputValue(formatQuantiteEditable(capped, article));
    }
  };

  // ── Helper text ──────────────────────────────────────────────────────────
  // Visible uniquement en focus pour ne pas décaler la hauteur du formulaire.
  // L'espace ' ' (non-breaking) est retourné hors focus pour que MUI
  // réserve toujours la hauteur du helper — évite le layout shift.

  const helperText = (() => {
    if (error) return error;
    if (!usePcsCts || !ppc || !isFocused) return ' ';

    // Si la saisie contient un séparateur valide → affiche la lecture humaine
    const sepIdx = findSeparatorIndex(inputValue);
    if (sepIdx > 0) {
      const c = parseInt(inputValue.slice(0, sepIdx));
      const p = parseInt(inputValue.slice(sepIdx + 1));
      if (!isNaN(c) && !isNaN(p) && p >= 0 && p < ppc)
        return `${c} ${uniteCarton} ${p} ${unitePiece}`;
    }

    // Sinon → indique le format attendu
    return `${uniteCarton}-${unitePiece} (ex: 1-3)`;
  })();

  // ── Rendu ────────────────────────────────────────────────────────────────
  // Le Box position:relative est nécessaire pour que le helper text
  // en position:absolute ne déplace pas les éléments voisins.

  return (
    <Box sx={{ position: 'relative' }}>
      <TextField
        {...rest}
        label={label}
        value={inputValue}
        size={size}
        fullWidth={fullWidth}
        disabled={disabled}
        // type="text" en mode cts-pcs pour accepter le tiret séparateur
        type={usePcsCts ? 'text' : 'number'}
        error={!!error}
        helperText={helperText}
        placeholder={usePcsCts ? 'ex: 2-5' : undefined}
        slotProps={{
          ...(usePcsCts
            ? {}
            : { htmlInput: { min: 0, max, step: 'any', style: { textAlign: 'right' } } }),
          // Helper text sorti du flux pour ne pas pousser les éléments voisins
          formHelperText: { sx: { position: 'absolute', top: '100%', mt: '2px', whiteSpace: 'nowrap' } },
        }}
        sx={{ '& .MuiFormHelperText-root': { position: 'absolute' } }}
        onChange={(e) => handleChange(e.target.value)}
        onFocus={(e) => {
          focused.current = true;
          setIsFocused(true);
          // Auto-sélection du contenu : la frappe écrase directement l'ancienne valeur.
          e.target.select();
        }}
        onBlur={handleBlur}
      />
    </Box>
  );
};

export default QuantiteInput;
